import { memo, useCallback, useMemo, useState } from 'react'
import Form from 'react-bootstrap/Form'
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'
import { describeAmendChanges } from '../../utils/appointment-note-addendum.utils'
import NoteChangeSummary from './NoteChangeSummary'
import './styles.scss'

/**
 * Last step of an edit. Shows only what will actually be written, so a field the
 * ops cannot express never appears here as a pending change.
 */
const AmendReview: React.FC = () => {
  const {
    editBaseline,
    editValues,
    noteForPatient,
    editOps,
    selectedFiles,
    isLoading,
    saveEdit,
    cancelEdit,
  } = usePostAppointmentContext()

  const [reason, setReason] = useState('')

  const changes = useMemo(
    () =>
      editBaseline ? describeAmendChanges(editBaseline, editValues) : undefined,
    [editBaseline, editValues]
  )

  const handleSave = useCallback(() => saveEdit(reason), [saveEdit, reason])

  const addedFiles = selectedFiles.flatMap((list) => Array.from(list))
  const hasChanges =
    (editOps.length > 0 && !!changes?.any) || addedFiles.length > 0

  return (
    <div className="amend-review align-self-center d-flex flex-column align-items-center w-100 mt-3">
      <div className="container d-flex flex-column gap-3">
        <p className="label mb-0">Summary of changes</p>

        {hasChanges && changes ? (
          <NoteChangeSummary changes={changes} />
        ) : (
          <p className="description mb-0">
            No changes yet. Go back to update orders, follow-up or the note.
          </p>
        )}

        {addedFiles.length > 0 && (
          <div className="note-attachments">
            <p className="attachment-heading mb-2">Attachments to add</p>
            <div className="attachment-list">
              {addedFiles.map((file, index) => (
                <div key={index} className="attachment-row">
                  <InsertDriveFileOutlined
                    className="attachment-icon"
                    aria-hidden="true"
                  />
                  <div className="attachment-details">
                    <span className="attachment-name">{file.name}</span>
                    <span className="attachment-status is-pending">
                      Pending upload
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="label mb-2">Note for patient:</p>
          <p className="note-preview mb-0">{noteForPatient}</p>
        </div>

        <div>
          <p className="label mb-1">Reason for edit</p>
          <p className="description mb-2">(optional, shown in edit history)</p>
          <Form.Control
            as="textarea"
            id="amendReason"
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setReason(event.target.value)
            }
            placeholder="Reason for edit"
            rows={2}
            value={reason}
          />
        </div>

        <p className="description mb-0">
          This updates the note without notifying the member (no message or
          email is sent). An entry with your name, the time and the changes
          above is added to this note&apos;s edit history.
        </p>

        <div className="d-flex gap-3 justify-content-end">
          <SecondaryButton onClick={cancelEdit} theme="dark">
            Cancel edit
          </SecondaryButton>
          <PrimaryButton
            disabled={!hasChanges || isLoading}
            onClick={handleSave}
            theme="dark"
          >
            Save &amp; update note
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}

export default memo(AmendReview)
